'use client';

import { Children, type ForwardRefExoticComponent, forwardRef } from 'react';

import { CarouselHeader } from './carousel-header';
import { CarouselHeadingArea } from './carousel-heading-area';
import { CarouselNavControls } from './carousel-nav-controls';
import { CarouselRoot } from './carousel-root';
import { CarouselScrollContainer } from './carousel-scroll-container';
import { CarouselSkipLink } from './carousel-skip-link';
import { CarouselSlide } from './carousel-slide';
import { useCarousel } from './hooks/use-carousel';
import type { CarouselPropsInterface } from './types/carousel.types';

export const Carousel = forwardRef<HTMLDivElement, CarouselPropsInterface>(
  (props: CarouselPropsInterface, forwardedRef) => {
    const {
      children,
      css: cssProp,
      heading,
      includeSkipLink = true,
      onNextClick = () => {},
      onPreviousClick = () => {},
      onScroll = () => {},
      onVisibleSlidesChange,
      enableSnap = { default: false, lg: true },
      hasFocusableContent = true,
      scrollOffset = 'none',
      scrollLeft = 0,
      scrollToSlide,
      slideGap = 'sm',
      slideAlign = 'start',
      slideWidth = 'auto',
      renderNavigationControls = (leftButtonProps, rightButtonProps) => (
        <CarouselNavControls
          leftButtonProps={leftButtonProps}
          rightButtonProps={rightButtonProps}
        />
      ),
      ...rest
    } = props;

    const SKIP_TO_END_TEXT = 'Skip to the end of the carousel';
    const SKIP_TO_BEGINNING_TEXT = 'Skip to the beginning of the carousel';

    const {
      previousEnabled,
      nextEnabled,
      nextPage,
      previousPage,
      firstSlide,
      scrollContainer,
      sectionStart,
      sectionEnd,
      handleOnScroll,
      scrollToFocusedEl,
      handleSkipToStart,
      handleSkipToEnd,
    } = useCarousel({
      onNextClick,
      onPreviousClick,
      onScroll,
      onVisibleSlidesChange,
      scrollLeft,
      scrollToSlide,
    });

    const cells = Children.map(children, (child, index) => (
      <CarouselSlide
        ref={index === 0 ? firstSlide : undefined}
        key={`carousel-child-${index}`}
        slideAlign={slideAlign}
        style={{ flexBasis: slideWidth }}
      >
        {child}
      </CarouselSlide>
    ));

    return (
      <CarouselRoot css={cssProp} {...rest} ref={forwardedRef}>
        <CarouselHeader>
          <CarouselHeadingArea>{heading}</CarouselHeadingArea>
          {renderNavigationControls &&
            renderNavigationControls(
              {
                onClick: previousPage,
                disabled: !previousEnabled,
              },
              {
                onClick: nextPage,
                disabled: !nextEnabled,
              },
            )}
        </CarouselHeader>
        {includeSkipLink ? (
          <CarouselSkipLink onClick={handleSkipToEnd} ref={sectionStart}>
            {SKIP_TO_END_TEXT}
          </CarouselSkipLink>
        ) : null}
        <CarouselScrollContainer
          enableSnap={enableSnap}
          hasFocusableContent={hasFocusableContent}
          onKeyUp={scrollToFocusedEl}
          onScroll={handleOnScroll}
          scrollOffset={scrollOffset}
          slideGap={slideGap}
          ref={scrollContainer}
        >
          {cells}
        </CarouselScrollContainer>
        {includeSkipLink ? (
          <CarouselSkipLink onClick={handleSkipToStart} ref={sectionEnd}>
            {SKIP_TO_BEGINNING_TEXT}
          </CarouselSkipLink>
        ) : null}
      </CarouselRoot>
    );
  },
) as ForwardRefExoticComponent<CarouselPropsInterface> & {
  Header: typeof CarouselHeader;
  HeadingArea: typeof CarouselHeadingArea;
  NavControls: typeof CarouselNavControls;
  Root: typeof CarouselRoot;
  ScrollContainer: typeof CarouselScrollContainer;
  SkipLink: typeof CarouselSkipLink;
  Slide: typeof CarouselSlide;
};

Carousel.displayName = 'Carousel';

Carousel.Header = CarouselHeader;
Carousel.HeadingArea = CarouselHeadingArea;
Carousel.NavControls = CarouselNavControls;
Carousel.Root = CarouselRoot;
Carousel.ScrollContainer = CarouselScrollContainer;
Carousel.SkipLink = CarouselSkipLink;
Carousel.Slide = CarouselSlide;
